/*
 *   File : dream-home.js
 *   Author : https://evoqins.com
 *   Description : Dream home goal page
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useState, useEffect } from "react";

// *********** Third Party ***************//
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//
import { CustomAmountInput, CustomSlider, CustomTextInput } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Footer } from "../../Component/Footer";
import { BreadCrumbs } from "../../Component/Other";
import { Icon } from "../../Component/Icon";
import { ChangeBasketNameModal } from "../../Component/Modal";
import GoalSummary from "./goal-summary";
import { APIService } from "../../Service";
import { _formatCorpus, _formatLac, _formatToRupees, _getAchieveByText, _getHomeFutureCost, _getGoalRequiredSip, _getHomeLoanEmi, _setGoal } from "../../Helper";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LeftPillarImage from "../../Assets/Images/GoalNew/goal-left-banner.png";
import RightPillarImage from "../../Assets/Images/GoalNew/goal-right-banner.png";
import CheckboxActive from "../../Assets/Images/GoalNew/checkbox-active.svg";
import CheckboxInactive from "../../Assets/Images/GoalNew/checkbox-inactive.svg";

const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Life goals",
        path: "/goals"
    },
];

const DEFAULT_HOME_COST = 5000000;
const DEFAULT_YEARS_TO_BUY = "6";
const DEFAULT_DOWN_PAYMENT_PERCENTAGE = "20";
const DEFAULT_SAVED_AMOUNT = 1000000;

const MAX_HOME_COST = 100000000;
const HOME_COST_MIN = 500000;
const HOME_COST_STEP = 100000;
const MAX_SAVED_AMOUNT = 100000000;
const SAVED_AMOUNT_STEP = 10000;
const MAX_YEARS_TO_BUY = 30;
const MAX_DOWN_PAYMENT_PERCENTAGE = 100;
const REGISTRATION_RATE = 8;

const MIN_TIMELINE_FILL_PERCENT = 2.7;

// Fixed assumptions of the home plan, the property inflation rate comes from the goal detail
const DEFAULT_PROPERTY_INFLATION_RATE = 7;
const SHORT_TERM_YEARS = 5;
const SHORT_TERM_RETURN = 9;
const LONG_TERM_RETURN = 12;
const LOAN_RATE = 8.5;
const LOAN_YEARS = 20;

const DreamHome = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [goalId, setGoalId] = useState("");
    const [goalDetail, setGoalDetail] = useState({});
    const [loading, setLoading] = useState(true);

    const [homeCost, setHomeCost] = useState(DEFAULT_HOME_COST);
    const [yearsToBuy, setYearsToBuy] = useState(DEFAULT_YEARS_TO_BUY);
    const [downPaymentPercentage, setDownPaymentPercentage] = useState(DEFAULT_DOWN_PAYMENT_PERCENTAGE);
    const [includeRegistration, setIncludeRegistration] = useState(true);
    const [savedAmount, setSavedAmount] = useState(DEFAULT_SAVED_AMOUNT);
    const [isSavingGoal, setIsSavingGoal] = useState(false);
    const [savedGoalId, setSavedGoalId] = useState(null);
    const [goalName, setGoalName] = useState("");
    const [showChangeNameModal, setShowChangeNameModal] = useState(false);
    const [isNameUpdating, setIsNameUpdating] = useState(false);
    const [goalSummary, setGoalSummary] = useState(false);

    useEffect(() => {

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (location.state !== null) {
            setGoalId(location.state.goal_id);
        } else {
            navigate("/goals");
        };

        if (location.state?.is_summary === false) {
            setGoalSummary(false);
        }

    }, [location]);

    useEffect(() => {
        if (goalId !== "") {
            _getGoalDetails();
        }
    }, [goalId]);

    // The summary step is kept in the url, so that a reload stays on it
    useEffect(() => {
        if (goalSummary === true) {
            navigate(`${location.pathname}#configure`, {
                state: {
                    goal_id: goalId,
                }
            });
        }
    }, [goalSummary]);

    useEffect(() => {
        if (showChangeNameModal) {
            new Modal(document.getElementById("change-basket-name-modal")).show();
        }
    }, [showChangeNameModal]);

    const breadcrumbs = [...BREADCRUMBS, { label: goalDetail.admin_configured?.name }];

    // Property inflation rate (%) comes from the goal detail
    const propertyInflationRate = goalDetail.admin_configured?.meta_data?.inflation_rate ?? DEFAULT_PROPERTY_INFLATION_RATE;

    const years = Number(yearsToBuy) || 0;
    const downPaymentRate = Number(downPaymentPercentage) || 0;
    const savedAmountToday = Number(savedAmount) || 0;
    const buyYear = new Date().getFullYear() + years;

    // The SIP is invested for the years to buy, below SHORT_TERM_YEARS it earns the lower return
    let investmentReturn = LONG_TERM_RETURN;
    if (years < SHORT_TERM_YEARS) {
        investmentReturn = SHORT_TERM_RETURN;
    }

    // The home cost grows with the property inflation, the down payment and registration are taken on that future cost
    const registrationRate = includeRegistration ? REGISTRATION_RATE : 0;
    const futureHomeCost = _getHomeFutureCost(homeCost, propertyInflationRate, years);
    const downPaymentAmount = futureHomeCost * downPaymentRate / 100;
    const targetAmount = futureHomeCost * (downPaymentRate + registrationRate) / 100;
    const monthlySip = _getGoalRequiredSip(targetAmount, savedAmountToday, investmentReturn, years);

    // The bank funds the rest of the home, there is no loan on a 100% down payment
    const hasLoan = downPaymentRate < MAX_DOWN_PAYMENT_PERCENTAGE;
    const loanAmount = futureHomeCost - downPaymentAmount;
    const loanEmi = _getHomeLoanEmi(loanAmount, LOAN_RATE, LOAN_YEARS);

    let needText = "the down payment";
    if (downPaymentRate === MAX_DOWN_PAYMENT_PERCENTAGE) {
        needText = "the full house";
    }
    if (includeRegistration) {
        needText += " and registration";
    }

    const downPaymentText = _formatLac(downPaymentAmount).replace(" ", "");
    const achieveByText = _getAchieveByText(years);

    // The timeline fills as the amount saved grows towards the amount needed
    let savedProgressPercent = 0;
    if (targetAmount > 0) {
        savedProgressPercent = Math.min(savedAmountToday / targetAmount * 100, 100);
    }
    const timelineFillPercent = Math.max(savedProgressPercent, MIN_TIMELINE_FILL_PERCENT);

    //handle back
    const _handleBack = () => {
        navigate("/goals");
    };

    const _handleHomeCostChange = (value) => {
        setHomeCost(value);
    };

    const _handleYearsToBuyChange = (value) => {
        setYearsToBuy(value);
    };

    const _handleDownPaymentChange = (value) => {
        setDownPaymentPercentage(value);
    };

    const _handleToggleRegistration = () => {
        setIncludeRegistration((isIncluded) => !isIncluded);
    };

    const _handleSavedAmountChange = (value) => {
        setSavedAmount(value);
    };

    const _handleOpenChangeNameModal = () => {
        setShowChangeNameModal(true);
    };

    const _handleChangeGoalName = (name) => {
        _renameGoal(name, false);
    };

    const _handleSkipGoalName = () => {
        _renameGoal("", true);
    };

    const _handleCloseChangeNameModal = () => {
        setIsNameUpdating(false);
        setShowChangeNameModal(false);
    };

    // API - Rename goal
    const _renameGoal = (name, skip) => {
        let api_version = 3;
        let is_post = true;
        let url = "goal/rename";
        let data = {
            "goal_id": savedGoalId,
            "skip": true,
        };

        if (skip === false) {
            data = {
                "goal_id": savedGoalId,
                "name": name,
                "skip": false,
            };
        }

        setIsNameUpdating(true);

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                if (skip === false) {
                    setGoalName(name);
                }
                document.getElementById("change-basket-name-modal-close")?.click();
                setGoalSummary(true);
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsNameUpdating(false);
        });
    };

    // API - Save the home goal, the amount saved already goes as the lumpsum
    const _handleSaveGoal = () => {
        if (years < 1) {
            toast.dismiss();
            toast.error("Enter the number of years to buy the home in", { className: "e-toast" });
            return;
        }

        if (targetAmount <= 0) {
            toast.dismiss();
            toast.error("Enter valid details to calculate the amount you need", { className: "e-toast" });
            return;
        }

        if (savedAmountToday > targetAmount) {
            toast.dismiss();
            toast.error("Amount needed should be greater than the amount saved", { className: "e-toast" });
            return;
        }

        const params = {
            "goal_id": goalId,
            "target": Math.round(targetAmount),
            "sip": monthlySip || null,
            "lumpsum": savedAmountToday || null,
            "sip_tenure": years,
            "lumpsum_select": 1,
            // The amount needed is already worked out in the inflated value of the buying year
            "inflation_select": false,
            "additional_data": {
                "add_lumpsum_now": savedAmountToday > 0,
                "home_cost": Number(homeCost),
                "years_to_buy": years,
                "down_payment_percentage": downPaymentRate,
                "include_registration": includeRegistration,
                "saved_amount": savedAmountToday,
                "monthly_sip": monthlySip,
                "return_rate": investmentReturn,
            },
        };

        setIsSavingGoal(true);

        _setGoal(params).then((response) => {
            if (response.status_code === 200) {
                setSavedGoalId(response.data.goal_id);
                toast.dismiss();
                toast.success("Goal saved", { className: "e-toast" });
                _handleOpenChangeNameModal();
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsSavingGoal(false);
        });
    };

    //API- Post goal detail
    const _getGoalDetails = () => {
        let is_post = true;
        let url = 'goal/get';
        let data = {
            "goal_id": goalId,
            "is_invested": false
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setGoalDetail(response.data);
                _fillGoalInputs(response.data);

                // Preserving current step if user reloads page
                if (location.hash) {
                    setGoalSummary(true);
                }
            } else {
                setGoalDetail({});
            }
            setLoading(false);
        });
    };

    // Fills the inputs from the saved goal (user_configured)
    const _fillGoalInputs = (goal) => {
        const saved_inputs = goal.user_configured?.additional_data || {};

        setHomeCost(saved_inputs.home_cost || DEFAULT_HOME_COST);
        setYearsToBuy(String(saved_inputs.years_to_buy || DEFAULT_YEARS_TO_BUY));
        setDownPaymentPercentage(String(saved_inputs.down_payment_percentage || DEFAULT_DOWN_PAYMENT_PERCENTAGE));
        setIncludeRegistration(saved_inputs.include_registration ?? true);
        setSavedAmount(saved_inputs.saved_amount ?? DEFAULT_SAVED_AMOUNT);
    };

    // Stays rendered on the summary step until the modal has finished closing
    const changeNameModal = showChangeNameModal &&
        <ChangeBasketNameModal title="Change goal name"
            skipButtonName="Skip"
            basketName={goalName || goalDetail.admin_configured?.name || ""}
            loading={isNameUpdating}
            onChangeName={_handleChangeGoalName}
            onSkip={_handleSkipGoalName}
            onClose={_handleCloseChangeNameModal} />;

    // Goal summary step
    if (goalSummary) {
        return (
            <>
                <GoalSummary goal_name={goalDetail.admin_configured?.short_name}
                    savedGoalId={savedGoalId}
                    customGoalName={goalName}
                    isLumpsum={savedAmountToday > 0}
                    tenure={{ min: 1, max: MAX_YEARS_TO_BUY }}
                    inflationSelect={false} />

                {changeNameModal}

                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">

                {loading ?
                    <div className="min-h-100vh text-center mt-5" >
                        <img src={Loader}
                            draggable="false"
                            alt="loader" />
                    </div>
                    :
                    <div className="col-lg-11 p-0 pb-4 pb-sm-5">
                        {/* breadcrumbs */}
                        <BreadCrumbs data={breadcrumbs} />

                        {/* Page title */}
                        <header className="d-flex align-items-center gap-sm-3 gap-2 my-sm-4 my-3 ">
                            <button type="button"
                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                aria-label="Back to life goals"
                                onClick={_handleBack}>
                                <Icon icon="arrow-back-left" size={24} className="width-20px-res" />
                            </button>
                            <h1 className="mb-0 e-text-chinese-black e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                Home down payment
                            </h1>
                        </header>

                        <div className="row">
                            <div className="col-12 col-md-8 col-xl-6">
                                <section className="retirement-goal-card e-bg-white e-border-radius-16 position-relative overflow-hidden pb-4">

                                    {/* Goal, the pillars end with the plan */}
                                    <div className="retirement-goal-top">
                                        <img src={LeftPillarImage}
                                            width={70}
                                            height={455}
                                            className="retirement-pillar retirement-pillar-left d-none d-sm-block"
                                            draggable="false"
                                            alt="" />
                                        <img src={RightPillarImage}
                                            width={70}
                                            height={455}
                                            className="retirement-pillar retirement-pillar-right d-none d-sm-block"
                                            draggable="false"
                                            alt="" />

                                        <div className="retirement-goal-content position-relative px-sm-4 pb-1">
                                            <header className="px-0 px-sm-5 padding-18px-top text-center">
                                                <h2 className="mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-30px">
                                                    Save for your <span className="e-heading-font-ivy e-font-weight-400 e-text-cobalt-ink">dream home</span>
                                                </h2>
                                                <p className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-13 e-font-12-res">
                                                    Grow your wealth steadily to make your dream of owning a home a reality
                                                </p>
                                            </header>

                                            {/* Projection */}
                                            <article className="retirement-projection e-border-1px-baby-blue e-border-radius-28 p-3 p-sm-4 mt-4">
                                                <div className="d-flex justify-content-between gap-3">
                                                    <div>
                                                        <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                            Invest
                                                        </p>
                                                        <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                            ₹{_formatToRupees(monthlySip)}
                                                            <span className="ms-1 e-text-jet-gray e-font-weight-500 e-font-12 line-height-16px">
                                                                monthly
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="text-end">
                                                        <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                            Downpayment
                                                        </p>
                                                        <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                            ₹{downPaymentText}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="retirement-timeline mt-4"
                                                    role="progressbar"
                                                    aria-label="Amount saved compared to the down payment"
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                    aria-valuenow={Math.round(savedProgressPercent)}>
                                                    <span className="retirement-timeline-fill"
                                                        style={{ width: `${timelineFillPercent}%` }}></span>
                                                </div>
                                                <p className="d-flex justify-content-between gap-2 mt-2 mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-14 e-font-12-res line-height-18px">
                                                    <span>Start today</span>
                                                    <span>
                                                        Achieve by <span className="e-text-ink-gray e-font-weight-500">{achieveByText}</span>
                                                    </span>
                                                </p>

                                                {/* What is needed */}
                                                <p className="mb-0 e-bg-white e-border-radius-24 e-box-shadow-soft-card p-3 mt-3 e-text-eerie-black e-primary-font-inter e-font-weight-400 e-font-12 line-height-16px">
                                                    You will need <span className="e-font-weight-600">₹{_formatCorpus(targetAmount)}</span> for {needText} in <span className="e-font-weight-600">{buyYear}</span>. This SIP gets you there.
                                                </p>
                                            </article>
                                        </div>
                                    </div>
                                </section>

                                {/* Inputs */}
                                <div className="px-sm-4">
                                    <div className="d-flex flex-column gap-4 mt-3">
                                        <div>
                                            <label className="d-flex justify-content-between align-items-center gap-2">
                                                <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                    Cost of the house today
                                                </span>
                                                <CustomAmountInput variant={4}
                                                    className="w-190px flex-shrink-0"
                                                    inputClassName="e-bg-white"
                                                    borderClassName="e-border-1px-gainsboro-gray"
                                                    value={homeCost}
                                                    maxValue={MAX_HOME_COST}
                                                    errorMsg=""
                                                    handleChange={_handleHomeCostChange} />
                                            </label>
                                            <div className="h-4 margin-top-20px mb-2">
                                                <CustomSlider type={2}
                                                    min={HOME_COST_MIN}
                                                    max={MAX_HOME_COST}
                                                    step={HOME_COST_STEP}
                                                    defaultValue={Number(homeCost)}
                                                    handleChange={_handleHomeCostChange} />
                                            </div>
                                        </div>

                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Buy home in
                                            </span>
                                            <CustomTextInput variant={4}
                                                type="number"
                                                suffix="Year(s)"
                                                value={yearsToBuy}
                                                maxLength={2}
                                                maxValue={MAX_YEARS_TO_BUY}
                                                hasBottomMargin={false}
                                                borderClassName="e-border-1px-white-marble"
                                                wrapperClassName="w-190px flex-shrink-0"
                                                inputWrapperBorderStyle="e-border-radius-12"
                                                errorMsg=""
                                                successMsg=""
                                                handleChange={_handleYearsToBuyChange} />
                                        </label>

                                        <div className="d-flex flex-column gap-3">

                                            <label className="d-flex justify-content-between align-items-center gap-2">
                                                <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                    Downpayment required
                                                </span>
                                                <CustomTextInput variant={4}
                                                    type="number"
                                                    suffix="%"
                                                    value={downPaymentPercentage}
                                                    maxLength={3}
                                                    maxValue={MAX_DOWN_PAYMENT_PERCENTAGE}
                                                    hasBottomMargin={false}
                                                    borderClassName="e-border-1px-white-marble"
                                                    wrapperClassName="w-190px flex-shrink-0"
                                                    inputWrapperBorderStyle="e-border-radius-12"
                                                    errorMsg=""
                                                    successMsg=""
                                                    handleChange={_handleDownPaymentChange} />


                                            </label>

                                            {/* Registration and stamp duty */}
                                            <label htmlFor="dream-home-registration"
                                                className="d-flex align-items-center gap-2 e-bg-white e-border-1px-gainsboro-gray e-border-radius-8 p-2 cursor-pointer">
                                                <input id="dream-home-registration"
                                                    className="visually-hidden"
                                                    type="checkbox"
                                                    checked={includeRegistration}
                                                    onChange={_handleToggleRegistration} />
                                                <img src={includeRegistration ? CheckboxActive : CheckboxInactive}
                                                    width={16}
                                                    height={16}
                                                    draggable="false"
                                                    alt="" />
                                                <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px">
                                                    Add registration and stamp duty ({REGISTRATION_RATE}%)
                                                </span>
                                            </label>
                                        </div>



                                        <div>
                                            <label className="d-flex justify-content-between align-items-center gap-2">
                                                <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                    Saved for this already
                                                </span>
                                                <CustomAmountInput variant={4}
                                                    className="w-190px flex-shrink-0"
                                                    inputClassName="e-bg-white"
                                                    borderClassName="e-border-1px-gainsboro-gray"
                                                    value={savedAmount}
                                                    maxValue={MAX_SAVED_AMOUNT}
                                                    errorMsg=""
                                                    handleChange={_handleSavedAmountChange} />
                                            </label>
                                            <div className="h-4 margin-top-20px mb-2">
                                                <CustomSlider type={2}
                                                    min={0}
                                                    max={MAX_SAVED_AMOUNT}
                                                    step={SAVED_AMOUNT_STEP}
                                                    defaultValue={Number(savedAmount) || 0}
                                                    handleChange={_handleSavedAmountChange} />
                                            </div>
                                        </div>

                                        {/* EMI on the remaining loan */}
                                        {
                                            hasLoan &&
                                            <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px">
                                                On the remaining ₹{_formatCorpus(loanAmount)} loan, expect an EMI of ₹{_formatToRupees(loanEmi)}/month at {LOAN_RATE}% over {LOAN_YEARS} years.
                                            </p>
                                        }
                                    </div>

                                    <div className="margin-top-32px">
                                        <PrimaryButton name="Save this goal"
                                            className="w-100 padding-12px-top padding-12px-bottom"
                                            loading={isSavingGoal}
                                            disabled={isSavingGoal}
                                            onPress={_handleSaveGoal} />
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                }
            </div>

            {changeNameModal}

            {/* footer  */}
            <Footer />
        </>
    )
}
export default DreamHome;
